import { Button, EmptyState, Skeleton } from '@paguz/ui';
import type { QueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { CompassIcon } from 'lucide-react';
import { useEffect } from 'react';
import { ensureSession } from './auth/ensure-session.ts';
import { useSessionState } from './auth/session.ts';
import { BottomNav, MobileFrame } from './components/layout.tsx';
import { LoginPage } from './features/auth/login-page.tsx';
import { SignupPage } from './features/auth/signup-page.tsx';
import { CHECKOUT_STEPS, type CheckoutStep } from './features/checkout/draft.ts';
import { HomePage } from './features/home/home-page.tsx';

/**
 * Rutas por código. Ingreso e Inicio van en el paquete inicial; el resto se carga por ruta
 * (`lazyRouteComponent`, con precarga al pasar el dedo/cursor por un enlace).
 */
const AgentPage = lazyRouteComponent(() => import('./features/agent/agent-page.tsx'), 'AgentPage');
const CheckoutPage = lazyRouteComponent(
  () => import('./features/checkout/checkout-page.tsx'),
  'CheckoutPage',
);
const ProcessingPage = lazyRouteComponent(
  () => import('./features/checkout/processing-page.tsx'),
  'ProcessingPage',
);
const DrawdownPage = lazyRouteComponent(
  () => import('./features/disputes/drawdown-with-extras.tsx'),
  'DrawdownWithExtras',
);
const MovementsPage = lazyRouteComponent(
  () => import('./features/credit/movements-page.tsx'),
  'MovementsPage',
);
const StatementPage = lazyRouteComponent(
  () => import('./features/credit/statement-page.tsx'),
  'StatementPage',
);
const DisputeFormPage = lazyRouteComponent(
  () => import('./features/disputes/dispute-form-page.tsx'),
  'DisputeFormPage',
);
const DisputesPage = lazyRouteComponent(
  () => import('./features/disputes/disputes-page.tsx'),
  'DisputesPage',
);
const DisputeDetailPage = lazyRouteComponent(
  () => import('./features/disputes/disputes-page.tsx'),
  'DisputeDetailPage',
);
const ApplicationPage = lazyRouteComponent(
  () => import('./features/origination/application-page.tsx'),
  'ApplicationPage',
);
const OfferPage = lazyRouteComponent(
  () => import('./features/origination/offer-page.tsx'),
  'OfferPage',
);
const GuidePage = lazyRouteComponent(() => import('./features/guide/guide-page.tsx'), 'GuidePage');
const ProfilePage = lazyRouteComponent(
  () => import('./features/profile/profile-page.tsx'),
  'ProfilePage',
);

export interface RouterContext {
  queryClient: QueryClient;
}

/** Solo rutas internas (evita redirecciones abiertas). */
export function safeRedirect(value: unknown): string | undefined {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : undefined;
}

function NotFound() {
  return (
    <EmptyState
      className="mt-10"
      icon={<CompassIcon aria-hidden />}
      title="No encontramos esta página"
      action={
        <Button asChild>
          <Link to="/">Ir al inicio</Link>
        </Button>
      }
    />
  );
}

function RoutePending() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Cargando">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <MobileFrame>
      <Outlet />
    </MobileFrame>
  ),
  notFoundComponent: NotFound,
});

/** Guía del demo: abierta con o sin sesión. */
const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guia',
  component: GuidePage,
});

// ── Públicas (sin sesión) ─────────────────────────────────────────────────
const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  beforeLoad: async () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- patrón de TanStack Router
    if ((await ensureSession()) === 'authenticated') throw redirect({ to: '/' });
  },
  component: () => (
    <main className="flex flex-1 flex-col">
      <Outlet />
    </main>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/ingresar',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = safeRedirect(search.redirect);
    return target === undefined ? {} : { redirect: target };
  },
  component: function LoginRoute() {
    const { redirect: target } = loginRoute.useSearch();
    return <LoginPage redirectTo={target} />;
  },
});

const signupRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/registro',
  component: SignupPage,
});

// ── Privadas (guard de sesión) ────────────────────────────────────────────
function AppLayout() {
  const { status } = useSessionState();
  const navigate = useNavigate();
  // Si la sesión se pierde (refresh rechazado o cierre de sesión), vuelve al ingreso.
  useEffect(() => {
    if (status === 'anonymous') void navigate({ to: '/ingresar', replace: true });
  }, [status, navigate]);
  return (
    <>
      <main className="flex flex-1 flex-col px-4 pt-4 pb-28">
        <Outlet />
      </main>
      <BottomNav />
    </>
  );
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ location }) => {
    if ((await ensureSession()) !== 'authenticated') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- patrón de TanStack Router
      throw redirect({
        to: '/ingresar',
        search: location.href === '/' ? {} : { redirect: location.href },
      });
    }
  },
  component: AppLayout,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });
const advisorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/asesor',
  validateSearch: (search: Record<string, unknown>): { agente?: 'compras' | 'asesor' } =>
    search.agente === 'compras' || search.agente === 'asesor' ? { agente: search.agente } : {},
  loader: () => AgentPage.preload?.(),
  component: function AdvisorRoute() {
    const { agente } = advisorRoute.useSearch();
    return <AgentPage {...(agente !== undefined && { choice: agente })} />;
  },
});
const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/perfil',
  component: ProfilePage,
});
const payRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pagar',
  validateSearch: (search: Record<string, unknown>): { paso?: CheckoutStep } => {
    const paso = search.paso;
    return typeof paso === 'string' && (CHECKOUT_STEPS as readonly string[]).includes(paso)
      ? { paso: paso as CheckoutStep }
      : {};
  },
  loader: () => CheckoutPage.preload?.(),
  component: function PayRoute() {
    const { paso } = payRoute.useSearch();
    return <CheckoutPage step={paso ?? 'origen'} />;
  },
});
const processingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pagar/$intentId',
  loader: () => ProcessingPage.preload?.(),
  component: function ProcessingRoute() {
    const { intentId } = processingRoute.useParams();
    return <ProcessingPage key={intentId} intentId={intentId} />;
  },
});
const movementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/movimientos',
  component: MovementsPage,
});
const drawdownRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compras/$drawdownId',
  loader: () => DrawdownPage.preload?.(),
  component: function DrawdownRoute() {
    const { drawdownId } = drawdownRoute.useParams();
    return <DrawdownPage drawdownId={drawdownId} />;
  },
});
const disputeFormRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compras/$drawdownId/reclamo',
  loader: () => DisputeFormPage.preload?.(),
  component: function DisputeFormRoute() {
    const { drawdownId } = disputeFormRoute.useParams();
    return <DisputeFormPage drawdownId={drawdownId} />;
  },
});
const disputesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reclamos',
  component: DisputesPage,
});
const disputeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reclamos/$disputeId',
  loader: () => DisputeDetailPage.preload?.(),
  component: function DisputeRoute() {
    const { disputeId } = disputeRoute.useParams();
    return <DisputeDetailPage disputeId={disputeId} />;
  },
});
const applicationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/solicitud',
  component: ApplicationPage,
});
const offerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/solicitud/oferta',
  component: OfferPage,
});
const statementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/extracto',
  component: StatementPage,
});

const routeTree = rootRoute.addChildren([
  guideRoute,
  publicRoute.addChildren([loginRoute, signupRoute]),
  appRoute.addChildren([
    homeRoute,
    advisorRoute,
    profileRoute,
    payRoute,
    processingRoute,
    movementsRoute,
    drawdownRoute,
    statementRoute,
    disputeFormRoute,
    disputesRoute,
    disputeRoute,
    applicationRoute,
    offerRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPendingComponent: RoutePending,
    defaultPendingMs: 150,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
